import { Theme } from '@mui/material/styles/createTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { addMinutes } from 'date-fns';
import { Formik } from 'formik';

import { Dialog } from '../../components/Dialog';
import { Stack } from '../../components/Stack';
import { getUserDisplayName } from '../../models/user/utils';
import { FormAutocomplete as Autocomplete } from '../../shared/Forms/Fields/FormAutocomplete';
import { FormDateTimePicker as DateTimePicker } from '../../shared/Forms/Fields/FormDateTimePicker';
import { FormTextField as TextField } from '../../shared/Forms/Fields/FormTextField';

import { useEditAppointmentDialog } from './useEditAppointmentDialog';

import type { Appointment } from '../../models/appointment/_types';

interface Props {
  open: boolean;
  appointment?: Appointment;
  onClose: () => void;
}

export const EditAppointmentDialog: React.FC<Props> = ({
  open,
  appointment,
  onClose,
}) => {
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const {
    services,
    servicesLoading,
    trainers,
    locations,
    locationsLoading,
    formik,
    getTrainerFromId,
    getTrainerOptions,
  } = useEditAppointmentDialog({
    appointment,
    onClose,
  });
  return (
    <Formik {...formik}>
      {({ submitForm, values, setFieldValue, resetForm }) => (
        <Dialog
          title={appointment?.id ? 'Edit Session' : 'New Session'}
          open={open}
          fullScreen={mdDown}
          onClose={() => {
            onClose();
            resetForm();
          }}
          actions={[
            {
              label: 'Save',
              fullWidth: true,
              color: 'primary',
              variant: 'contained',
              onClick: submitForm,
            },
          ]}
        >
          <Stack spacing={3} sx={{ pt: 1 }}>
            <TextField
              name="serviceId"
              label="Service"
              placeholder="Select Service"
              options={
                servicesLoading
                  ? []
                  : services?.map((service) => {
                      return { label: service.name, value: service.id };
                    })
              }
              onChange={(event) => {
                const serviceId = event.target.value;
                const duration = (services || []).find(
                  (service) => service.id === serviceId
                )?.defaultTime;
                if (values.startTime && duration) {
                  setFieldValue(
                    'endTime',
                    addMinutes(values.startTime, duration)
                  );
                }
              }}
            />
            {trainers && (
              <Autocomplete
                name="primaryTrainerIds"
                multiple
                fullWidth
                options={getTrainerOptions(values.secondaryTrainerIds).map(
                  (e) => e.id
                )}
                getOptionLabel={(option) =>
                  getUserDisplayName(getTrainerFromId(option)) || ''
                }
                label="Primary Trainers"
                placeholder="Select Trainers"
              />
            )}
            {trainers && (
              <Autocomplete
                name="secondaryTrainerIds"
                multiple
                fullWidth
                options={getTrainerOptions(values.primaryTrainerIds).map(
                  (e) => e.id
                )}
                getOptionLabel={(option) =>
                  getUserDisplayName(getTrainerFromId(option)) || ''
                }
                label="Secondary Trainers"
                placeholder="Select Trainers"
              />
            )}
            <DateTimePicker
              name="startTime"
              label="Start time"
              onChange={(date) => {
                const duration =
                  (services || []).find(
                    (service) => service.id === values.serviceId
                  )?.defaultTime || 60;
                if (date && duration) {
                  setFieldValue('endTime', addMinutes(date, duration));
                }
              }}
            />
            <DateTimePicker name="endTime" label="End time" />
            <TextField
              name="locationId"
              label="Location"
              placeholder="Select Location"
              options={
                locationsLoading || !locations
                  ? []
                  : locations?.locations?.map((location) => {
                      return { label: location.name, value: location.id };
                    })
              }
            />
            <TextField
              name="description"
              label="Description (public)"
              placeholder="Enter your description"
              multiline
            />
            <TextField
              name="notes"
              label="Notes (private to trainer)"
              placeholder="Enter notes"
              multiline
            />
          </Stack>
        </Dialog>
      )}
    </Formik>
  );
};
